import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ImageOff, Search, SearchX, Star, Store } from 'lucide-react';
import { cn, formatCOP, formatDistance } from '@/lib/cn';
import { useBusqueda } from '@/lib/queries';
import { puntoDeBusqueda, useUbicacion } from '@/lib/ubicacion';
import { Cargando, Entrada, Vacio } from '@/components/ui';

export function Buscar() {
  const [params, setParams] = useSearchParams();
  const consulta = params.get('q') ?? '';
  const [texto, setTexto] = useState(consulta);

  const ubicacion = useUbicacion((s) => s.ubicacion);
  const punto = puntoDeBusqueda(ubicacion);
  const resultados = useBusqueda(consulta, punto);

  /** Escribir actualiza la URL con retardo: así el atrás del navegador funciona. */
  useEffect(() => {
    const temporizador = setTimeout(() => {
      if (texto.trim() === consulta) return;
      setParams(texto.trim() ? { q: texto.trim() } : {}, { replace: true });
    }, 350);
    return () => clearTimeout(temporizador);
  }, [texto, consulta, setParams]);

  const vacio = consulta.trim().length < 2;
  const sinResultados = !vacio && resultados.data?.total === 0;

  return (
    <div className="mx-auto max-w-3xl pb-20 sm:pb-0">
      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-kick-gray" />
        <Entrada
          type="search"
          autoFocus
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Busca un comercio o un plato"
          aria-label="Buscar comercios o productos"
          className="py-3.5 pl-12 text-base"
        />
      </div>

      {vacio && (
        <div className="mt-8">
          <Vacio
            icono={<Search className="h-6 w-6" />}
            titulo="¿Qué se te antoja?"
            descripcion="Escribe al menos dos letras. Buscamos a la vez entre comercios y productos."
          />
        </div>
      )}

      {resultados.isLoading && <Cargando texto="Buscando…" />}

      {sinResultados && (
        <div className="mt-8">
          <Vacio
            icono={<SearchX className="h-6 w-6" />}
            titulo={`Sin resultados para «${consulta}»`}
            descripcion="Prueba con otra palabra o revisa la ortografía."
          />
        </div>
      )}

      {resultados.data && resultados.data.total > 0 && (
        <div className="mt-6 space-y-8">
          {resultados.data.merchants.length > 0 && (
            <section>
              <h2 className="mb-3 text-lg text-kick-dark">
                Comercios ({resultados.data.merchants.length})
              </h2>
              <ul className="space-y-2">
                {resultados.data.merchants.map((comercio) => (
                  <li key={comercio.id}>
                    <Link
                      to={`/c/${comercio.slug}`}
                      className={cn(
                        'card-kick flex items-center gap-3 p-3',
                        !comercio.isOpen && 'opacity-70',
                      )}
                    >
                      {comercio.logoUrl ? (
                        <img
                          src={comercio.logoUrl}
                          alt=""
                          loading="lazy"
                          className="h-12 w-12 shrink-0 rounded-kick object-cover"
                        />
                      ) : (
                        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-kick bg-kick-light text-kick-gray/40">
                          <Store className="h-5 w-5" />
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-kick-ink">{comercio.name}</p>
                        <p className="truncate text-sm text-kick-gray">
                          {comercio.categoryName}
                          {comercio.distanceMeters !== undefined &&
                            ` · ${formatDistance(comercio.distanceMeters)}`}
                        </p>
                      </div>

                      <div className="shrink-0 text-right">
                        {comercio.ratingAvg > 0 && (
                          <span className="flex items-center gap-0.5 text-sm">
                            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                            {comercio.ratingAvg.toFixed(1)}
                          </span>
                        )}
                        {!comercio.isOpen && (
                          <span className="text-xs text-kick-gray">Cerrado</span>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {resultados.data.products.length > 0 && (
            <section>
              <h2 className="mb-3 text-lg text-kick-dark">
                Productos ({resultados.data.products.length})
              </h2>
              <ul className="grid gap-2 sm:grid-cols-2">
                {resultados.data.products.map((producto) => (
                  <li key={producto.id}>
                    <Link
                      to={`/c/${producto.merchantSlug}`}
                      className={cn(
                        'card-kick flex gap-3 p-3',
                        !producto.merchantIsOpen && 'opacity-70',
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-kick-ink">{producto.name}</p>
                        <p className="truncate text-sm text-kick-gray">
                          {producto.merchantName}
                        </p>
                        <p className="mt-1 font-bold text-kick-dark">
                          {formatCOP(producto.price)}
                        </p>
                      </div>

                      {producto.imageUrl ? (
                        <img
                          src={producto.imageUrl}
                          alt=""
                          loading="lazy"
                          className="h-16 w-16 shrink-0 rounded-kick bg-kick-light object-contain"
                        />
                      ) : (
                        <div className="grid h-16 w-16 shrink-0 place-items-center rounded-kick bg-kick-light text-kick-gray/40">
                          <ImageOff className="h-5 w-5" />
                        </div>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
